import {
  Column,
  Columns,
  Loader,
  Stack,
  Strong,
  Text,
  TextDropdown,
} from 'braid-design-system';
import { useTranslations } from '@vocab/react';
import translations from './.vocab';
import type { Reviews, SortOrder } from '@seek/libs-shared';
import type { FlagReviewPayload } from '../../';
import { ReviewRow } from '../reviewRow/ReviewRow';
import { useEffect, useState } from 'react';

interface Props {
  model?: Reviews;
  upvoteReview: ({
    reviewId,
    companyId,
  }: {
    reviewId: string;
    companyId: string;
  }) => Promise<void>;
  flagReview: (
    companyId: string,
    reviewId: string,
    payload: FlagReviewPayload,
  ) => Promise<boolean>;
  sortOrder: SortOrder;
  onSortOrderChange: (value: SortOrder) => void;
}

export const ReviewsList = ({
  model,
  upvoteReview,
  flagReview,
  onSortOrderChange,
  sortOrder,
}: Props) => {
  const { t } = useTranslations(translations);
  const [modifiedUpvotedReviewsIds, setModifiedUpvotedReviewsIds] = useState<
    string[]
  >(model?.upvotedIds?.upvotes || []);
  const [newUpvotedReviewsIds, setNewUpvotedReviewsIds] = useState<string[]>(
    [],
  );

  useEffect(() => {
    setModifiedUpvotedReviewsIds((r) => [
      ...r,
      ...(model?.upvotedIds?.upvotes || []),
    ]);
  }, [model?.upvotedIds?.upvotes]);

  if (!model) {
    return <Loader />;
  }
  const total = model?.paging?.total || 0;

  const injectModelOnUpvote = async ({
    reviewId,
    companyId,
  }: {
    reviewId: string;
    companyId: string;
  }): Promise<void> => {
    setModifiedUpvotedReviewsIds([...modifiedUpvotedReviewsIds, reviewId]);
    setNewUpvotedReviewsIds([...newUpvotedReviewsIds, reviewId]);
    await upvoteReview({ reviewId, companyId });
  };

  return (
    <Stack space="large">
      <Columns space="small" collapseBelow="tablet">
        <Column>
          <Text size="small">
            {t('Showing <Strong>{total}</Strong> reviews sorted by', {
              Strong: (children) => <Strong>{children}</Strong>,
              total: `${total}`,
            })}{' '}
            <Strong>{t(`${sortOrder}`)}</Strong>
          </Text>
        </Column>
        <Column width="content">
          <Text size="small">
            {t('Sort by')}{' '}
            <Strong>
              <TextDropdown
                id="sortDropdown"
                label={t('Sort by')}
                value={sortOrder}
                onChange={onSortOrderChange}
                options={[
                  {
                    text: t('most recent'),
                    value: 'most recent',
                  },
                  {
                    text: t('most helpful'),
                    value: 'most helpful',
                  },
                ]}
              />
            </Strong>
          </Text>
        </Column>
      </Columns>
      <Stack space="small">
        {model.reviews?.map((review, index) => (
          <ReviewRow
            key={index}
            model={review}
            isOnReviewPage={false}
            upvoteReview={injectModelOnUpvote}
            newUpvotedReviewsIds={newUpvotedReviewsIds}
            flagReview={flagReview}
            upvotedReviewsIds={modifiedUpvotedReviewsIds}
            index={index}
          />
        ))}
      </Stack>
    </Stack>
  );
};
